// apps/office/src/pages/office/InteriorClients.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search,
  Phone,
  Mail,
  MapPin,
  Home,
  FolderOpen,
  ChevronRight,
  Loader2,
  Users,
  Palette,
  Plus,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  XCircle,
  FileText,
  Wallet,
  LayoutGrid,
  List,
  Sparkles,
  UserCheck,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import pb from '../../lib/pocketbaseClient';
import InteriorProjectForm from '../../components/office/InteriorProjectForm';

const rs = (v) => {
  const n = Number(v) || 0;
  return `Rs. ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
};

const STATUS_PRIORITY = {
  Active: 5,
  'On Hold': 4,
  Completed: 3,
  Draft: 2,
  Cancelled: 1,
};

const getClientStatus = (projects) => {
  if (!projects || projects.length === 0) return 'Draft';
  const present = [...new Set(projects.map((p) => p.status || 'Draft'))];
  present.sort((a, b) => (STATUS_PRIORITY[b] || 0) - (STATUS_PRIORITY[a] || 0));
  return present[0] || 'Draft';
};

const COUNTABLE_STATUSES = ['Active', 'On Hold', 'Completed'];

const STATUS_COLORS = {
  Active: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'On Hold': 'bg-amber-100 text-amber-700 border-amber-300',
  Completed: 'bg-blue-100 text-blue-700 border-blue-300',
  Draft: 'bg-slate-100 text-slate-600 border-slate-300',
  Cancelled: 'bg-red-100 text-red-700 border-red-300',
};

const STATUS_BAND = {
  Active: 'bg-emerald-500',
  'On Hold': 'bg-amber-500',
  Completed: 'bg-blue-500',
  Cancelled: 'bg-red-500',
  Draft: 'bg-slate-400',
};

const VIEW_KEY = 'interior-clients-view-style';

/* ─── Check if client needs conversion ───
   Returns TRUE if client is NOT converted (button should show)
   Returns FALSE if already converted (button hidden)
*/
const isClientNotConverted = (client) => {
  if (!client) return false;
  if (client.status === 'Converted') return false;
  const jobId = String(client.jobId || '').toUpperCase();
  if (jobId.startsWith('HD-')) return false;
  if (jobId.startsWith('TEMP-')) return true;
  return true;
};

const InteriorClients = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [viewStyle, setViewStyle] = useState('list'); // 'list' | 'grid'

  /* ── Load saved preference ── */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(VIEW_KEY);
      if (saved === 'list' || saved === 'grid') {
        setViewStyle(saved);
      }
    } catch {}
  }, []);

  const changeView = (style) => {
    setViewStyle(style);
    try { localStorage.setItem(VIEW_KEY, style); } catch {}
  };

  const { data: projects = [], isLoading, error, refetch } = useQuery({
    queryKey: ['interior-projects-all'],
    queryFn: async () => {
      const records = await pb.collection('interior_projects').getFullList({
        sort: '-created',
        expand: 'clientId',
      });
      return records;
    },
  });

  /* ── Group + aggregate ── */
  /* ── Fetch all approved quotations ── */
  const { data: allApprovedQuotes = [] } = useQuery({
    queryKey: ['interior-quotations-approved-all'],
    queryFn: () => pb.collection('interior_quotations').getFullList({
      filter: 'isLatest = true && status = "Approved"',
      fields: 'id,projectId,total',
    }),
    enabled: projects.length > 0,
  });

  const clientGroups = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      const cid = p.clientId;
      if (!cid) return;
      if (!map[cid]) {
        map[cid] = {
          clientId: cid,
          client: p.expand?.clientId || null,
          projects: [],
          createdAt: p.created,
        };
      }
      map[cid].projects.push(p);
    });

    return Object.values(map).map((g) => {
      const active = g.projects.filter((p) => p.status === 'Active');
      const onHold = g.projects.filter((p) => p.status === 'On Hold');
      const completed = g.projects.filter((p) => p.status === 'Completed');
      const draft = g.projects.filter((p) => p.status === 'Draft');
      const cancelled = g.projects.filter((p) => p.status === 'Cancelled');

      const countable = [...active, ...onHold, ...completed];

      /* Approved quote sum for this client's projects */
      const projectQuoteMap = {};
      allApprovedQuotes.forEach(q => {
        if (q.projectId) {
          projectQuoteMap[q.projectId] = (projectQuoteMap[q.projectId] || 0) + (Number(q.total) || 0);
        }
      });

      const totalQuote = countable.reduce((s, p) => s + (projectQuoteMap[p.id] || 0), 0);
      const totalPaid = countable.reduce((s, p) => s + (Number(p.paidAmount) || 0), 0);
      const totalDue = Math.max(0, totalQuote - totalPaid);

      return {
        clientId: g.clientId,
        client: g.client,
        projects: g.projects,
        projectCount: g.projects.length,
        status: getClientStatus(g.projects),
        hasCountableProjects: countable.length > 0,
        totalQuote,
        totalPaid,
        totalDue,
        createdAt: g.createdAt,
      };
    }).sort((a, b) => {
      const pa = STATUS_PRIORITY[a.status] || 0;
      const pb_ = STATUS_PRIORITY[b.status] || 0;
      if (pa !== pb_) return pb_ - pa;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [projects, allApprovedQuotes]);


  /* ─── Convert Client to Converted status ─── */
  const convertClient = useMutation({
    mutationFn: async ({ clientId, currentJobId, clientName }) => {
      let newJobId = null;
      // If job ID is TEMP-*, generate a converted HD-* job ID
      if (currentJobId && currentJobId.startsWith('TEMP-')) {
        try {
          const { getNextConvertedJobId } = await import('../../lib/jobIdUtils');
          newJobId = await getNextConvertedJobId();
        } catch (e) {
          console.warn('[InteriorClients] jobId generation failed:', e);
        }
      }

      const payload = { status: 'Converted' };
      if (newJobId) payload.jobId = newJobId;

      return pb.collection('leads').update(clientId, payload);
    },
    onSuccess: (_, vars) => {
      toast.success(`✅ ${vars.clientName} converted${vars.currentJobId?.startsWith('TEMP-') ? ' — new HD ID assigned' : ''}`);
      // Invalidate everything client-related
      queryClient.invalidateQueries(['interior-projects-all']);
      queryClient.invalidateQueries(['interior-clients-list']);
      queryClient.invalidateQueries(['leads']);
      queryClient.invalidateQueries(['interior-clients-list']);
      queryClient.invalidateQueries(['interior-client', vars.clientId]);
      // Force refetch to get fresh expand data
      setTimeout(() => refetch(), 300);
    },
    onError: (err) => {
      let msg = err.message || 'Convert failed';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      toast.error('❌ ' + msg);
    },
  });

  /* ── Global stats ── */
  const stats = useMemo(() => {
    const active = projects.filter((p) => p.status === 'Active').length;
    const completed = projects.filter((p) => p.status === 'Completed').length;
    const cancelled = projects.filter((p) => p.status === 'Cancelled').length;
    const draft = projects.filter((p) => p.status === 'Draft').length;

    const countable = projects.filter((p) => COUNTABLE_STATUSES.includes(p.status));

    /* Approved quote sum */
    const projectQuoteMap = {};
    allApprovedQuotes.forEach(q => {
      if (q.projectId) {
        projectQuoteMap[q.projectId] = (projectQuoteMap[q.projectId] || 0) + (Number(q.total) || 0);
      }
    });

    const totalQuote = countable.reduce((s, p) => s + (projectQuoteMap[p.id] || 0), 0);
    const totalPaid = countable.reduce((s, p) => s + (Number(p.paidAmount) || 0), 0);
    const totalDue = Math.max(0, totalQuote - totalPaid);

    return {
      active, completed, cancelDraft: cancelled + draft,
      totalQuote, totalPaid, totalDue,
      totalProjects: projects.length,
      totalClients: clientGroups.length,
    };
  }, [projects, clientGroups, allApprovedQuotes]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    if (!s) return clientGroups;
    return clientGroups.filter(
      (g) =>
        g.client?.name?.toLowerCase().includes(s) ||
        g.client?.jobId?.toLowerCase().includes(s) ||
        g.client?.phone?.toString().includes(s) ||
        g.client?.email?.toLowerCase().includes(s)
    );
  }, [clientGroups, search]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500 font-medium">Failed to load interior projects</p>
        <p className="text-xs text-slate-500 mt-2">{error.message}</p>
        <Button variant="outline" className="mt-4" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* ── Header ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-6 sm:p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              <Home className="w-7 h-7" /> Interior Projects
            </h1>
            <p className="text-white/80 text-sm mt-1">
              Clients with active interior projects
            </p>
          </div>
          <Button
            onClick={() => setFormOpen(true)}
            className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg gap-2 font-semibold"
          >
            <Plus className="w-4 h-4" /> New Project
          </Button>
        </div>
      </div>

      {/* ── Project count stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <FolderOpen className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold">Total</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.totalProjects}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
              <p className="text-[10px] uppercase text-emerald-700 tracking-wide font-semibold">Active</p>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
              <p className="text-[10px] uppercase text-blue-700 tracking-wide font-semibold">Completed</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.completed}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-3.5 h-3.5 text-red-600" />
              <p className="text-[10px] uppercase text-red-700 tracking-wide font-semibold">Cancel+Draft</p>
            </div>
            <p className="text-2xl font-bold text-red-600">{stats.cancelDraft}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-3.5 h-3.5 text-slate-500" />
              <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold">Clients</p>
            </div>
            <p className="text-2xl font-bold text-slate-800">{stats.totalClients}</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-3.5 h-3.5 text-indigo-600" />
              <p className="text-[10px] uppercase text-indigo-700 tracking-wide font-semibold">Collected</p>
            </div>
            <p className="text-2xl font-bold text-indigo-600">
              {stats.totalQuote > 0
                ? ((stats.totalPaid / stats.totalQuote) * 100).toFixed(0)
                : 0}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Amount stats ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md overflow-hidden">
          <div className="h-1 bg-indigo-500" />
          <CardContent className="p-5 flex justify-between items-center">
            <div>
              <p className="text-xs uppercase text-slate-500 tracking-wide font-semibold">Total Quotation</p>
              <p className="text-2xl font-bold text-slate-800 mt-1">{rs(stats.totalQuote)}</p>
            </div>
            <div className="bg-indigo-100 p-3 rounded-xl">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md overflow-hidden">
          <div className="h-1 bg-emerald-500" />
          <CardContent className="p-5 flex justify-between items-center">
            <div>
              <p className="text-xs uppercase text-slate-500 tracking-wide font-semibold">Total Paid</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{rs(stats.totalPaid)}</p>
            </div>
            <div className="bg-emerald-100 p-3 rounded-xl">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md overflow-hidden">
          <div className="h-1 bg-red-500" />
          <CardContent className="p-5 flex justify-between items-center">
            <div>
              <p className="text-xs uppercase text-slate-500 tracking-wide font-semibold">Total Due</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{rs(stats.totalDue)}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Search + View toggle ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, job ID, phone, email…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
          <button
            onClick={() => changeView('list')}
            title="List view"
            className={`p-2 rounded-md transition-all ${
              viewStyle === 'list'
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            onClick={() => changeView('grid')}
            title="Card view"
            className={`p-2 rounded-md transition-all ${
              viewStyle === 'grid'
                ? 'bg-white text-emerald-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Empty state ── */}
      {filtered.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-20 text-center">
            <Home className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">
              {clientGroups.length === 0 ? 'No interior projects yet' : 'No clients match your search'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {clientGroups.length === 0
                ? 'Click "New Project" to create the first one'
                : 'Try a different search term'}
            </p>
            {clientGroups.length === 0 && (
              <Button
                onClick={() => setFormOpen(true)}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              >
                <Plus className="w-4 h-4" /> New Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewStyle === 'list' ? (
        /* ═══════════ LIST VIEW ═══════════ */
        <Card className="border-0 shadow-md overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="text-slate-600 font-semibold">Client</TableHead>
                  <TableHead className="text-slate-600 font-semibold">Status</TableHead>
                  <TableHead className="text-slate-600 font-semibold">Projects</TableHead>
                  <TableHead className="text-slate-600 font-semibold text-right">Quote</TableHead>
                  <TableHead className="text-slate-600 font-semibold text-right">Paid</TableHead>
                  <TableHead className="text-slate-600 font-semibold text-right">Due</TableHead>
                  <TableHead className="text-slate-600 font-semibold text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((g) => {
                  const client = g.client;
                  const status = g.status;
                  const statusClass = STATUS_COLORS[status] || STATUS_COLORS.Draft;

                  return (
                    <TableRow
                      key={g.clientId}
                      className="hover:bg-emerald-50/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/office/interior-projects/client/${g.clientId}`)}
                    >
                      {/* Client */}
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                            {(client?.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-800 truncate">
                              {client?.name || 'Unknown'}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {client?.jobId && (
                                <span className="text-[10px] font-mono text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                                  {client.jobId}
                                </span>
                              )}
                              {client?.propertyType && (
                                <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                  {client.propertyType}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Badge className={`${statusClass} text-xs border`}>{status}</Badge>
                      </TableCell>

                      {/* Projects count */}
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <FolderOpen className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">{g.projectCount}</span>
                        </div>
                      </TableCell>

                      {/* Quote */}
                      <TableCell className="text-right">
                        {g.hasCountableProjects ? (
                          <span className="font-semibold text-slate-700">{rs(g.totalQuote)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>

                      {/* Paid */}
                      <TableCell className="text-right">
                        {g.hasCountableProjects ? (
                          <span className="font-semibold text-emerald-600">{rs(g.totalPaid)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>

                      {/* Due */}
                      <TableCell className="text-right">
                        {g.hasCountableProjects ? (
                          g.totalDue > 0 ? (
                            <span className="font-bold text-red-600">{rs(g.totalDue)}</span>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 gap-1 text-xs">
                              <CheckCircle className="w-3 h-3" /> Paid
                            </Badge>
                          )
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </TableCell>

                      {/* Action */}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Convert button — only when client's lead status is NOT Converted */}
                          {isClientNotConverted(client) && (
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!window.confirm(`Mark "${client?.name}" as Converted?\n${client?.jobId?.startsWith('TEMP-') ? 'A new HD-xxxx ID will be assigned.' : ''}`)) return;
                                convertClient.mutate({
                                  clientId: g.clientId,
                                  currentJobId: client?.jobId,
                                  clientName: client?.name,
                                });
                              }}
                              disabled={convertClient.isPending && convertClient.variables?.clientId === g.clientId}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 shadow-sm h-8 text-xs font-semibold"
                              title="Mark this client as Converted"
                            >
                              {convertClient.isPending && convertClient.variables?.clientId === g.clientId ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Sparkles className="w-3.5 h-3.5" />
                              )}
                              Convert
                            </Button>
                          )}


                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-emerald-600 hover:bg-emerald-50 gap-1 h-8 text-xs"
                          >
                            View <ChevronRight className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        /* ═══════════ CARD VIEW ═══════════ */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((g) => {
            const client = g.client;
            const status = g.status;
            const statusClass = STATUS_COLORS[status] || STATUS_COLORS.Draft;

            return (
              <Card
                key={g.clientId}
                className="border-0 shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden group"
                onClick={() => navigate(`/office/interior-projects/client/${g.clientId}`)}
              >
                <div className={`h-1.5 ${STATUS_BAND[status] || STATUS_BAND.Draft}`} />

                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {(client?.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-bold text-slate-800 truncate">
                        {client?.name || 'Unknown'}
                      </h3>
                      {client?.jobId && (
                        <span className="inline-block mt-0.5 text-[10px] font-mono text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">
                          {client.jobId}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap mb-3">
                    <Badge className={`${statusClass} text-xs border`}>{status}</Badge>
                    {client?.propertyType && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                        <Palette className="w-3 h-3 mr-1" />
                        {client.propertyType}
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-1 text-xs text-slate-600 mb-3">
                    {client?.phone && (
                      <div className="flex items-center gap-1.5">
                        <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="truncate">{client.phone}</span>
                      </div>
                    )}
                    {client?.email && (
                      <div className="flex items-center gap-1.5">
                        <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                        <span className="truncate">{client.email}</span>
                      </div>
                    )}
                    {client?.address && (
                      <div className="flex items-start gap-1.5">
                        <MapPin className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                        <span className="line-clamp-1">{client.address}</span>
                      </div>
                    )}
                  </div>

                  {g.hasCountableProjects ? (
                    <div className="grid grid-cols-3 gap-2 mb-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="text-center">
                        <p className="text-[9px] uppercase text-slate-500 font-semibold tracking-wide">Quote</p>
                        <p className="text-xs font-bold text-slate-700 mt-0.5">{rs(g.totalQuote)}</p>
                      </div>
                      <div className="text-center border-x border-slate-200">
                        <p className="text-[9px] uppercase text-emerald-600 font-semibold tracking-wide">Paid</p>
                        <p className="text-xs font-bold text-emerald-600 mt-0.5">{rs(g.totalPaid)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[9px] uppercase text-red-600 font-semibold tracking-wide">Due</p>
                        <p className="text-xs font-bold text-red-600 mt-0.5">
                          {g.totalDue > 0 ? rs(g.totalDue) : '—'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-3 p-2.5 rounded-lg bg-slate-50 border border-dashed border-slate-200 text-center">
                      <p className="text-[10px] text-slate-400">No active projects — check details</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-1.5">
                      <FolderOpen className="w-4 h-4 text-emerald-600" />
                      <span className="text-xs font-medium text-slate-700">
                        {g.projectCount} {g.projectCount === 1 ? 'project' : 'projects'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {isClientNotConverted(client) && (
                        <Button
                          size="sm"
                          onClick={() => {
                            if (!window.confirm(`Mark "${client?.name}" as Converted?`)) return;
                            convertClient.mutate({
                              clientId: g.clientId,
                              currentJobId: client?.jobId,
                              clientName: client?.name,
                            });
                          }}
                          disabled={convertClient.isPending && convertClient.variables?.clientId === g.clientId}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 h-7 text-xs font-semibold px-2"
                        >
                          {convertClient.isPending && convertClient.variables?.clientId === g.clientId ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Sparkles className="w-3 h-3" />
                          )}
                          Convert
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-600 hover:bg-emerald-50 gap-0.5 h-7 text-xs px-2"
                      >
                        View <ChevronRight className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Form dialog ── */}
      <InteriorProjectForm
        open={formOpen}
        onOpenChange={setFormOpen}
        existingProject={null}
        onSuccess={() => {
          queryClient.invalidateQueries(['interior-projects']);
          queryClient.invalidateQueries(['interior-projects-all']);
          refetch();
        }}
      />
    </div>
  );
};

export default InteriorClients;
